import React, { Component } from "react";
import _ from "lodash";
import Loading from "../../common/components/loading/loading";
import { getOrganizationsByUser } from "../../common/services/auth/userAccountOrganizationService";
import { getOrganizations } from "../../common/services/auth/organizationService";
import { getTermsByDate } from "../../common/services/markets/termService";
import {
  getMarksByMarket,
  getAggregateMarksByMarket,
  getAggregateForwardMarksByMarket,
} from "../../common/services/marketdata/markService";
import { getMarketGroupMarkets } from "../../common/services/markets/marketGroupMarketService";
import {
  getFilteredProductForMarket,
  pivotQualityAttributes,
  refilterProductForMarketDateChange,
} from "../../common/services/products/productUtilities";
import { getMarketPriceDriverCombinations } from "../../common/services/markets/marketPriceDriverCombinationService";
import { getContributorSubscriptionsByMarket } from "../../common/services/marketdata/organizationSubscriptionService";
import { getUserAccountSubscriptionsByUserAccount } from "../../common/services/marketdata/userAccountSubscriptionService";
import {
  copyPriorMarks,
  generateAlbertaEnviromentalSettlements,
} from "../../common/services/marketdata/markGenerationService";
import MarksTable from "./marksTable";
import { withTranslation } from "react-i18next";
import ListGroup from "react-bootstrap/ListGroup";
import Select from "../../common/components/form/select";
import Input from "../../common/components/form/input";
import MarksHistoryTable from "../../marketData/marketData/marksHistoryTable";
import MarksForwardTable from "../../marketData/marketData/marksForwardTable";
import { dateStringIsToday, priorBusinessDay } from "../../common/services/utilities";
import {
  CommodityType,
  HistoricalReportingPeriodicity,
} from "../../common/enumerations/enums";
import { SHA256 } from "crypto-js";
import uuid from "react-uuid";
import { toast } from "react-toastify";

class MarketMarks extends Component {
  state = {
    marketAttributes: [],
    organizations: [],
    marks: [],
    historicalMarks: [],
    forwardMarks: [],
    terms: [],
    fromDate: null,
    toDate: null,
    originalQualityAttributeValues: null,
    filteredProduct: null,
    marketPriceDriverCombinations: [],
    organizationId: null,
    isLoading: true,
    marketDate: null,
    termScheduleId: null,
    priceBasisId: null,
    currencyId: null,
    uomId: null,
    isGeneratingMarks: false,
    pricePrecision: 2,
    volumePrecision: 0,
    currencySymbol: "$",
  };

  async componentDidMount() {
    await this.initialize();
  }

  async initialize() {
    this.loadMarks = this.loadMarks.bind(this);

    const { productId, market, allowEditOnBehalf, userAccountId } = this.props;
    const { marketId } = market;

    const marketDate = new Date().toLocaleDateString("fr-CA");

    const { venue, termSchedule } = this.props;
    const { forwardReportingPeriodicity, historicalReportingPeriodicity } =
      termSchedule;

    const [
      { originalQualityAttributeValues, filteredProduct },
      marketPriceDriverCombinations,
    ] = await Promise.all([
      getFilteredProductForMarket(productId, marketId, marketDate),
      getMarketPriceDriverCombinations(marketId).catch((error) => {
        console.error(
          "Error loading market price driver combinations:",
          error
        );
        return [];
      }),
    ]);

    //Build the organization select list.
    const myOrganizations = (
      await getOrganizationsByUser(userAccountId)
    ).filter((o) => o.active);
    const allOrganizations = allowEditOnBehalf
      ? (await getOrganizations())
          .filter((o) => o.active)
          .map(({ organizationId, name, active }) => ({
            organizationId,
            name,
            active,
          }))
      : [];

    const mySubscriptions = await getUserAccountSubscriptionsByUserAccount(
      userAccountId
    );

    const marketGroups = (await getMarketGroupMarkets()).filter(
      (m) => m.active
    );

    const subscribedItems = mySubscriptions.map((x) => x.subscribableId);

    const marketGroupMarkets = marketGroups
      .filter((m) => subscribedItems.includes(m.marketGroupId))
      .map((x) => x.marketId);

    const myMarketSubscriptions = mySubscriptions
      .filter(
        (s) =>
          subscribedItems.includes(marketId) ||
          marketGroupMarkets.includes(marketId)
      )
      .map((x) => x.organizationId);

    const allSubscriptions = allowEditOnBehalf
      ? (await getContributorSubscriptionsByMarket(marketId)).map(
          (x) => x.organizationId
        )
      : [];

    //Build the full list
    const organizationSuperset = [
      ...myOrganizations.filter(
        (o) =>
          myMarketSubscriptions.includes(o.organizationId) || allowEditOnBehalf
      ),
      ...allOrganizations.filter((o) =>
        allSubscriptions.includes(o.organizationId)
      ),
    ];

    //Remove any duplicates
    const organizations = _.uniqWith(organizationSuperset, _.isEqual);

    const timeZone =
      venue.timeZone +
      " (" +
      Intl.DateTimeFormat("us", {
        timeZoneName: "short",
        timeZone: venue.timeZone,
      })
        .formatToParts()
        .find((i) => i.type === "timeZoneName").value +
      ")";
    this.setState({
      originalQualityAttributeValues: originalQualityAttributeValues,
      filteredProduct: filteredProduct,
      marketPriceDriverCombinations: marketPriceDriverCombinations || [],
      organizations: organizations,
      organizationId: organizations[0].organizationId,
      marketDate: marketDate,
      marketAttributes: market.marketAttributes,
      termScheduleId: market.termScheduleId,
      termSchedule: termSchedule,
      forwardReportingPeriodicity: forwardReportingPeriodicity,
      historicalReportingPeriodicity: historicalReportingPeriodicity,
      priceBasisId: market.marketAttributes[0].priceBasisId,
      currencyId: market.marketAttributes[0].currencyId,
      uomId: market.marketAttributes[0].uomId,
      pricePrecision: market.marketAttributes[0].pricePrecision ?? 2,
      volumePrecision: market.marketAttributes[0].volumePrecision ?? 0,
      currencySymbol: market.marketAttributes[0].currencySymbol ?? "$",
      timeZone: timeZone,
      isLoading: false,
    });

    if (marketDate && market.termScheduleId) {
      await this.loadTerms(market.termScheduleId, marketDate);
    }

    if (marketDate && marketId && organizations) {
      await this.loadMarks(
        marketId,
        marketDate,
        organizations[0].organizationId
      );
    }
    if (
      marketDate &&
      termSchedule.historicalReportingPeriodicity &&
      allowEditOnBehalf
    ) {
      await this.loadMarketData(
        marketDate,
        termSchedule.historicalReportingPeriodicity
      );
    }
  }

  async loadTerms(termScheduleId, marketDate) {
    const terms = await getTermsByDate(termScheduleId, marketDate);

    this.setState({ terms: terms });
  }

  async loadMarks(marketId, marketDate, organizationId) {
    const marks = await getMarksByMarket(marketId, marketDate, organizationId);

    this.setState({ marks: marks });
  }

  async loadMarketData(marketDate, historicalReportingPeriodicity) {
    //Set the fromMarketDate
    const lookbackWindow = historicalReportingPeriodicity === "Hour" ? 1 : 7; // trailing week for Day, trailing 48 hours for Hour, other historical periodicities don't really apply.
    let fromDate = new Date(marketDate);
    fromDate.setMinutes(fromDate.getMinutes() + fromDate.getTimezoneOffset());
    fromDate.setDate(fromDate.getDate() - lookbackWindow);

    fromDate = priorBusinessDay(fromDate);

    const curveFromDate = new Date(marketDate);
    curveFromDate.setMinutes(
      curveFromDate.getMinutes() + curveFromDate.getTimezoneOffset()
    );
    curveFromDate.setDate(curveFromDate.getDate() - 1);

    const toDate = new Date(marketDate);
    toDate.setMinutes(toDate.getMinutes() + toDate.getTimezoneOffset());

    this.setState({ fromDate: fromDate, toDate: toDate });

    const { market } = this.props;
    const { marketId } = market;

    //await this.loadTrades(marketId, fromDate, toDate);
    await this.loadDashboardMarks(marketId, fromDate, toDate, curveFromDate);
    //await this.loadMarketPrices(marketId, toDate);
  }

  async loadDashboardMarks(marketId, fromDate, toDate, curveFromDate) {
    const historicalMarks = (
      await getAggregateMarksByMarket(
        marketId,
        fromDate.toLocaleDateString("fr-CA"),
        toDate.toLocaleDateString("fr-CA")
      )
    ).filter((x) => x.markState === "Approved" || x.markState === "Submitted");
    const forwardMarks = (
      await getAggregateForwardMarksByMarket(
        marketId,
        curveFromDate.toLocaleDateString("fr-CA"),
        toDate.toLocaleDateString("fr-CA")
      )
    ).filter((x) => x.markState === "Approved" || x.markState === "Submitted");

    this.setState({
      historicalMarks: historicalMarks,
      forwardMarks: forwardMarks,
    });
  }

  handleMarketDateChange = async (marketDate) => {
    // Update date immediately so input doesn't reset
    this.setState({ marketDate: marketDate });

    const {
      organizationId,
      termScheduleId,
      historicalReportingPeriodicity,
    } = this.state;
    const { market, allowEditOnBehalf } = this.props;
    const { marketId } = market;

    // Perform refiltering asynchronously in background
    refilterProductForMarketDateChange(this, marketDate, marketId);

    // Load data asynchronously (existing behavior)
    (async () => {
      await this.loadMarks(marketId, marketDate, organizationId);
    })();

    (async () => {
      await this.loadTerms(termScheduleId, marketDate);
    })();

    if (allowEditOnBehalf) {
      (async () => {
        await this.loadMarketData(marketDate, historicalReportingPeriodicity);
      })();
    }
  };

  handleOrganizationChange = (organizationId) => {
    this.setState({ organizationId: organizationId });

    const { marketDate } = this.state;
    const { market } = this.props;
    const { marketId } = market;

    (async () => {
      await this.loadMarks(marketId, marketDate, organizationId);
    })();
  };

  handleUpdate = (mark) => {
    var marks = [...this.state.marks];
    const i = marks.findIndex((_mark) => _mark.markId === mark.markId);
    if (i > -1) marks[i] = mark;
    else marks.push(mark);
    this.setState({ marks: marks });

    //Also update the marketData context views
    //This is not working properly on update due to no MarkId on the AggregateMarks model....
    const { allowEditOnBehalf } = this.props;
    if (allowEditOnBehalf) {
      var historicalMarks = [...this.state.historicalMarks];
      const j = historicalMarks.findIndex(
        (_mark) => _mark.markId === mark.markId
      );
      if (j > -1) historicalMarks[j] = mark;
      else historicalMarks.push(mark);
      this.setState({ historicalMarks: historicalMarks });

      var forwardMarks = [...this.state.forwardMarks];
      const k = forwardMarks.findIndex((_mark) => _mark.markId === mark.markId);
      if (k > -1) forwardMarks[k] = mark;
      else forwardMarks.push(mark);
      this.setState({ forwardMarks: forwardMarks });
    }
  };

  handleDelete = (markId) => {
    var marks = [...this.state.marks];
    const i = marks.findIndex((_mark) => _mark.markId === markId);
    if (i > -1) marks.pop(marks[i]);
    this.setState({ marks: marks });

    //Also update the marketData context views
    //This is not working properly on update due to no MarkId on the AggregateMarks model....
    const { allowEditOnBehalf } = this.props;
    if (allowEditOnBehalf) {
      var historicalMarks = [...this.state.historicalMarks];
      const j = historicalMarks.findIndex((_mark) => _mark.markId === markId);
      if (j > -1) historicalMarks.pop(historicalMarks[j]);
      this.setState({ historicalMarks: historicalMarks });

      var forwardMarks = [...this.state.forwardMarks];
      const k = forwardMarks.findIndex((_mark) => _mark.markId === markId);
      if (k > -1) forwardMarks.pop(forwardMarks[k]);
      this.setState({ forwardMarks: forwardMarks });
    }
  };

  handleBulkUpdate = (newMarks) => {
    var marks = [...this.state.marks];
    var historicalMarks = [...this.state.historicalMarks];
    var forwardMarks = [...this.state.forwardMarks];

    for (const mark of newMarks) {
      const i = marks.findIndex((_mark) => _mark.markId === mark.markId);
      if (i > -1) marks[i] = mark;
      else marks.push(mark);

      //Also update the marketData context views
      //This is not working properly on update due to no MarkId on the AggregateMarks model....
      const { allowEditOnBehalf } = this.props;
      if (allowEditOnBehalf) {
        const j = historicalMarks.findIndex(
          (_mark) => _mark.markId === mark.markId
        );
        if (j > -1) historicalMarks[j] = mark;
        else historicalMarks.push(mark);

        const k = forwardMarks.findIndex(
          (_mark) => _mark.markId === mark.markId
        );
        if (k > -1) forwardMarks[k] = mark;
        else forwardMarks.push(mark);
      }
    }

    this.setState({ marks: marks });
    this.setState({ historicalMarks: historicalMarks });
    this.setState({ forwardMarks: forwardMarks });
  };

  getQualityAttributeValuesByHash = () => {
    const { filteredProduct } = this.state;
    if (!filteredProduct) return {};

    const pivotedData = pivotQualityAttributes(filteredProduct);

    return pivotedData.reduce((qavByHash, row) => {
      const sortedQualityAttributeValues = (row.qualityAttributeValues || [])
        .map(
          (qualityAttributeValue) =>
            qualityAttributeValue.qualityAttributeValueId
        )
        .filter((value) => value !== undefined)
        .sort();

      const concatenatedValues = sortedQualityAttributeValues.join("");
      const qualityAttributeValueHash = SHA256(concatenatedValues)
        .toString()
        .toUpperCase();

      qavByHash[qualityAttributeValueHash] = row.qualityAttributeValues;
      return qavByHash;
    }, {});
  };

  handleGenerateMarks = async (commodityType) => {
    const { organizationId, termScheduleId, marketDate, terms } = this.state;
    const { market, t } = this.props;
    const { marketId } = market;

    if (!organizationId || !termScheduleId || !marketDate || !terms?.length) {
      return;
    }

    const qualityAttributeValuesByHash = this.getQualityAttributeValuesByHash();

    this.setState({ isGeneratingMarks: true });

    try {
      const payload = {
        MarketId: marketId,
        OrganizationId: organizationId,
        TermScheduleId: termScheduleId,
        MarketDate: marketDate,
        Terms: terms.map((term) => term.termId),
        QualityAttributeValuesByHash: qualityAttributeValuesByHash,
        EventId: uuid(),
      };

      let marksGenerated = [];
      switch (commodityType) {
        case CommodityType.None:
          marksGenerated = await copyPriorMarks(payload);
          break;
        case CommodityType.AlbertaEnvironmentals:
          marksGenerated = await generateAlbertaEnviromentalSettlements(payload);
          break;
        default:
          break;
      }

      if (marksGenerated !== undefined && marksGenerated.length > 0) {
        await this.loadMarks(marketId, marketDate, organizationId);
        this.handleBulkUpdate(marksGenerated);

        const marketDateDisplay = new Date(marketDate);
        toast.success(
          t(
            marksGenerated.length +
              " marks generated for date: " +
              marketDateDisplay.toDateString()
          )
        );
      } else {
        const marketDateDisplay = new Date(marketDate);
        toast.error(
          t("0 marks generated for date: " + marketDateDisplay.toDateString())
        );
      }
    } catch (ex) {
      toast.error(t(ex.request?.message || ex.message));
    } finally {
      this.setState({ isGeneratingMarks: false });
    }
  };

  handlemarketAttributeSelect = (marketAttribute) => {
    this.setState({
      priceBasisId: marketAttribute.priceBasisId,
      currencyId: marketAttribute.currencyId,
      uomId: marketAttribute.uomId,
    });
  };

  getFilteredMarks() {
    const { marks, priceBasisId, uomId, currencyId } = this.state;

    const filteredMarks = marks.filter(
      (x) =>
        x.priceBasisId === priceBasisId &&
        x.uomId === uomId &&
        x.currencyId === currencyId
    );

    return { marks: filteredMarks };
  }

  getFilteredHistoricalMarks() {
    const { historicalMarks, priceBasisId, uomId, currencyId } = this.state;

    const filteredMarks = historicalMarks.filter(
      (x) =>
        x.priceBasisId === priceBasisId &&
        x.uomId === uomId &&
        x.currencyId === currencyId
    );

    return { historicalMarks: filteredMarks };
  }

  getFilteredForwardMarks() {
    const { forwardMarks, priceBasisId, uomId, currencyId } = this.state;

    const filteredMarks = forwardMarks.filter(
      (x) =>
        x.priceBasisId === priceBasisId &&
        x.uomId === uomId &&
        x.currencyId === currencyId
    );

    return { forwardMarks: filteredMarks };
  }

  render() {
    const {
      organizations,
      terms,
      organizationId,
      timeZone,
      marketAttributes,
      termScheduleId,
      historicalReportingPeriodicity,
      forwardReportingPeriodicity,
      toDate,
      fromDate,
      isLoading,
      marketDate,
      priceBasisId,
      currencyId,
      uomId,
      isGeneratingMarks,
      pricePrecision,
      volumePrecision,
      currencySymbol,
      filteredProduct,
      marketPriceDriverCombinations,
    } = this.state;

    const product = filteredProduct;

    if (isLoading) return <Loading />;

    const {
      t,
      allowEdit,
      allowOverrideMarks,
      allowEditOnBehalf,
      allowGenerateMarks,
      allowCopyMarks,
      productId,
      market,
    } = this.props;
    const { marketId } = market;
    const marketName = (market?.name ?? market?.marketName ?? "").trim();
    const renderSectionTitle = (label) => (
      <span className="section-title-label">
        {label}
      </span>
    );

    const { marks } = this.getFilteredMarks();
    const { forwardMarks } = this.getFilteredForwardMarks();
    const { historicalMarks } = this.getFilteredHistoricalMarks();
    const isToday = dateStringIsToday(marketDate);

    var date = new Date();

    const endDate = date.toLocaleDateString("fr-CA");

    return (
      <div className="container-fluid market-data-page">
        {marketName && <h1 className="market-name-page-title">{marketName}</h1>}
        <div className="row">
          <div className="col-md-3"></div>
          <div className="col-md-6">
            <div className="selector-title-class">{t("PriceBasis")}</div>
            <ListGroup
              value={marketAttributes[0]}
              horizontal="sm"
              className="price-basis-selector"
            >
              {marketAttributes.map((attribute, index) => (
                <ListGroup.Item
                  className="market-attribute-selector"
                  key={index}
                  action
                  active={priceBasisId === attribute.priceBasisId}
                  onClick={() => this.handlemarketAttributeSelect(attribute)}
                >
                  {attribute.priceBasisName +
                    " (" +
                    attribute.currencyName +
                    "/" +
                    attribute.uomName +
                    ")"}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>{" "}
          <div className="col-md-3"></div>
        </div>
        <div className="row mt-4 market-marks-controls">
          <div className="col-md-2">
            <Input
              type="date"
              max={endDate}
              onChange={(e) =>
                this.handleMarketDateChange(e.currentTarget.value)
              }
              //name = "name"
              value={marketDate}
              label={
                <span className="selector-title-class">{t("MarketDate")}</span>
              }
            />
          </div>
          <div className="col-md-2">
            <Select
              idField="organizationId"
              name="name"
              disabled={Object.keys(organizations).length < 2}
              value={organizationId}
              label={
                <span className="selector-title-class">{t("Submitter")}</span>
              }
              options={organizations}
              onChange={(e) =>
                this.handleOrganizationChange(e.currentTarget.value)
              }
            />
          </div>
          <div className="col-md-2">
            <div className="selector-title-class time-zone-title">
              {t("TimeZone")}
            </div>
            <div>{timeZone}</div>
          </div>
          <div className="col-md-2"></div>
          {isToday && (allowCopyMarks || allowGenerateMarks) && (
            <div className="col-md-4 d-flex align-items-end justify-content-end pb-1 flex-wrap gap-1">
              {isGeneratingMarks ? (
                <div className="d-inline-block align-middle">
                  <span
                    className="spinner-border spinner-border-sm text-success"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="ms-2">{t("GeneratingMarks")}</span>
                </div>
              ) : (
                <>
                  {allowCopyMarks && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => this.handleGenerateMarks(CommodityType.None)}
                    >
                      {t("CopyPriorMarks")}
                    </button>
                  )}
                  {allowGenerateMarks && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() =>
                        this.handleGenerateMarks(
                          CommodityType.AlbertaEnvironmentals
                        )
                      }
                    >
                      {t("GenerateMarks")}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="col marks-table">
          <MarksTable
            submitCallbackFunction={this.handleUpdate}
            allowEdit={allowEdit}
            allowOverrideMarks={allowOverrideMarks}
            allowEditOnBehalf={allowEditOnBehalf}
            product={product}
            marketPriceDriverCombinations={marketPriceDriverCombinations}
            terms={terms}
            marks={marks}
            organizationId={organizationId}
            productId={productId}
            marketId={marketId}
            termScheduleId={termScheduleId}
            marketDate={marketDate}
            priceBasisId={priceBasisId}
            currencyId={currencyId}
            uomId={uomId}
            pricePrecision={pricePrecision}
            volumePrecision={volumePrecision}
            currencySymbol={currencySymbol}
            handleDelete={this.handleDelete}
          />
        </div>
        {allowEditOnBehalf && fromDate && toDate && (
          <div className="d-flex flex-wrap gap-1" style={{ paddingTop: "20px" }}>
            {historicalReportingPeriodicity !==
              HistoricalReportingPeriodicity.Year && (
              <div className="flex-fill tile section-tile shadow-card">
                <h5 className="section-title">
                  {renderSectionTitle(t("SpotHistory"))}
                </h5>
                <MarksHistoryTable
                  marks={historicalMarks}
                  product={product}
                  marketPriceDriverCombinations={marketPriceDriverCombinations}
                  marketId={marketId}
                  termScheduleId={termScheduleId}
                  historicalReportingPeriodicity={
                    historicalReportingPeriodicity
                  }
                  fromDate={fromDate}
                  toDate={toDate}
                  priceBasisId={priceBasisId}
                  currencyId={currencyId}
                  uomId={uomId}
                  pricePrecision={pricePrecision}
                  volumePrecision={volumePrecision}
                  currencySymbol={currencySymbol}
                />
              </div>
            )}
            <div className="flex-fill tile section-tile shadow-card">
              <h5 className="section-title">
                {renderSectionTitle(t("ForwardCurve"))}
              </h5>
              <MarksForwardTable
                marks={forwardMarks}
                product={product}
                marketPriceDriverCombinations={marketPriceDriverCombinations}
                marketId={marketId}
                termScheduleId={termScheduleId}
                forwardReportingPeriodicity={forwardReportingPeriodicity}
                marketDate={marketDate}
                priceBasisId={priceBasisId}
                currencyId={currencyId}
                uomId={uomId}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencySymbol={currencySymbol}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(MarketMarks);
